(() => {
  const SELECTORS = {
    button: '#find-in-store-button',
    drawer: '#find-in-store-drawer',
    locationsContainer: '#locations-container',
    locationTemplate: '#drawer-location-template',
    locationBlock: '.find-in-store-drawer__location-block',
    stockIcon: '.find-in-store-drawer__location-header__stock__icon',
    stockText: '.find-in-store-drawer__location-header__stock__text',
    favouriteButton: '.find-in-store-drawer__location-button',
    searchWrapper: '.find-in-store-drawer__search',
    searchInput: '.find-in-store-drawer__block__search-input',
    searchClear: '.find-in-store-drawer__search-clear',
    searchSuggestions: '.find-in-store-drawer__search-suggestions',
    searchSuggestion: '.find-in-store-drawer__search-suggestion',
    inStockCheckbox: '.find-in-store-drawer__block__checkbox',
    drawerSizeRadios: 'input[name="drawer-radio"]'
    // NOTE: the main size container selector is built per-instance in the
    // constructor because its class is localized (e.g. `--størrelse`).
  };

  const FUNCTIONS_BASE = 'https://netlify.blacklemon.dk/.netlify/functions';
  const ENDPOINTS = {
    locations: (store) =>
      `${FUNCTIONS_BASE}/getAllLocationsWithMetafield?store=${encodeURIComponent(store)}`,
    inventory: (store, prodId) =>
      `${FUNCTIONS_BASE}/getProductInventoryLevels?store=${encodeURIComponent(
        store
      )}&prodId=${encodeURIComponent(prodId)}`
  };

  const STORAGE_KEYS = {
    favouriteStore: 'favouriteStore',
    inStockOnly: 'findInStoreInStockOnly'
  };

  function $(root, selector) {
    return root.querySelector(selector);
  }

  function $all(root, selector) {
    return Array.from(root.querySelectorAll(selector));
  }

  function parseIntSafe(value, fallback = 0) {
    const n = Number.parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function getMetafieldValue(edges, key) {
    const edge = edges?.find((e) => e?.node?.key === key);
    return edge?.node?.value ?? '';
  }

  function debounce(fn, delayMs) {
    let t;
    return (...args) => {
      window.clearTimeout(t);
      t = window.setTimeout(() => fn(...args), delayMs);
    };
  }

  // Great-circle (linear) distance in km between two coordinates.
  function haversineKm(lat1, lng1, lat2, lng2) {
    if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return Infinity;
    const R = 6371; // Earth radius in km
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  // Install Google's dynamic-library bootstrap so `google.maps.importLibrary`
  // exists (required by Places API New). Idempotent — the real API overrides
  // the stub once the script finishes loading. Mirrors Google's official loader.
  function loadGoogleMaps(apiKey) {
    const google = window.google || (window.google = {});
    const maps = google.maps || (google.maps = {});
    if (maps.importLibrary) return; // already bootstrapped (or fully loaded)

    const libraries = new Set();
    let loadPromise;

    const startLoad = () => {
      if (loadPromise) return loadPromise;
      loadPromise = new Promise((resolve, reject) => {
        const params = new URLSearchParams({
          key: apiKey,
          v: 'weekly',
          language: 'da',
          region: 'DK',
          libraries: [...libraries].join(','),
          callback: 'google.maps.__findInStoreMapsCb__'
        });
        maps.__findInStoreMapsCb__ = resolve;
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
        script.async = true;
        script.onerror = () => reject(new Error('Google Maps could not load.'));
        document.head.appendChild(script);
      });
      return loadPromise;
    };

    // Stub: queue the requested library, trigger the load, then defer to the
    // real importLibrary the API installs on top of this one.
    maps.importLibrary = (name) => {
      libraries.add(name);
      return startLoad().then(() => maps.importLibrary(name));
    };
  }

  function escapeCss(value) {
    // `CSS.escape` is widely supported on modern browsers, but keep a safe fallback.
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/["\\#.:,[\]()>+~*^$|=\s]/g, '\\$&');
  }

  // Render plain-text lines into `el`, separated by real <br> elements.
  // Avoids injecting metafield/address strings as unsanitized HTML.
  function setTextLines(el, lines) {
    el.replaceChildren();
    lines.forEach((line, i) => {
      if (i > 0) el.appendChild(document.createElement('br'));
      el.appendChild(document.createTextNode(String(line)));
    });
  }

  class FindInStoreDrawer {
    constructor() {
      this.button = document.querySelector(SELECTORS.button);
      this.drawer = document.querySelector(SELECTORS.drawer);

      if (!this.button || !this.drawer) {
        this.disabled = true;
        return;
      }

      this.store = this.drawer.getAttribute('data-store') || '';
      this.prodId = this.drawer.getAttribute('data-prod-id') || '';
      this.initialVariantId = this.drawer.getAttribute('data-default-variant-id') || '';
      this.mapsApiKey = this.drawer.getAttribute('data-maps-api-key') || '';

      // The main variant picker derives its size container class from the
      // (localized) option name, e.g. `variant-picker__option-values--størrelse`.
      // Liquid passes us that suffix so we can find and sync with the real picker
      // regardless of store language. Falls back to "size" if not provided.
      const sizeOptionType = this.drawer.getAttribute('data-size-option-type') || 'size';
      this.mainSizeContainerSelector = `.product-info__block-item .variant-picker__option-values--${sizeOptionType}`;

      this.levelOut = parseIntSafe(this.drawer.getAttribute('data-out-of-stock-threshold'), 0);
      this.levelLow = parseIntSafe(this.drawer.getAttribute('data-low-stock-threshold'), 5);

      this.stockText = {
        in: this.drawer.getAttribute('data-in-stock-text') || 'In stock',
        low: this.drawer.getAttribute('data-low-stock-text') || 'Low stock',
        out: this.drawer.getAttribute('data-out-of-stock-text') || 'Out of stock'
      };

      this.inventoryByVariantId = new Map(); // variantId -> { inventory_quantities: [...] }
      this.observer = null;

      // All listeners are registered with this signal so destroy() can remove
      // them in one shot (see the theme-editor lifecycle handling at the bottom).
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.syncMainToDrawerDebounced = debounce(() => this.syncMainToDrawer(), 50);
    }

    init() {
      if (this.disabled) return;

      this.bindUIEvents();
      this.restoreInStockOnly();
      this.setupMainVariantSync();
      this.setupAddressSearch();

      // Load locations + inventory in parallel, then paint initial stock once DOM exists.
      Promise.all([this.loadLocations(), this.loadInventory()])
        .then(() => {
          const variantId = this.initialVariantId;
          if (variantId) this.renderStockForVariant(variantId);
          this.applyInStockOnlyFilter();
        })
        .catch((err) => {
          console.error('[find-in-store] init failed', err);
        });
    }

    bindUIEvents() {
      // Favourite store selection (event delegation)
      this.drawer.addEventListener('click', (e) => {
        const btn = e.target?.closest?.(SELECTORS.favouriteButton);
        if (!btn) return;
        this.toggleFavourite(btn);
      }, { signal: this.signal });

      // In-stock-only filter checkbox
      document.addEventListener('change', (e) => {
        if (!e.target?.matches?.(SELECTORS.inStockCheckbox)) return;
        // Persist the choice so it carries across products for this session.
        if (e.target.checked) {
          window.sessionStorage.setItem(STORAGE_KEYS.inStockOnly, 'true');
        } else {
          window.sessionStorage.removeItem(STORAGE_KEYS.inStockOnly);
        }
        this.applyInStockOnlyFilter();
      }, { signal: this.signal });

      // Variant change event (from theme)
      document.addEventListener('variant-changed', (e) => {
        const variantId = e?.detail?.variantId;
        if (!variantId) return;
        this.renderStockForVariant(String(variantId));
        this.applyInStockOnlyFilter();
      }, { signal: this.signal });
    }

    async setupAddressSearch() {
      const input = this.drawer.querySelector(SELECTORS.searchInput);
      const list = this.drawer.querySelector(SELECTORS.searchSuggestions);
      const wrapper = this.drawer.querySelector(SELECTORS.searchWrapper);
      const clearBtn = this.drawer.querySelector(SELECTORS.searchClear);
      // Markup is only rendered when a Maps API key is configured.
      if (!input || !list || !this.mapsApiKey) return;

      // Swap the search icon for the clear (close) icon once the field has text.
      const syncClearButton = () => {
        if (wrapper) wrapper.classList.toggle('has-value', input.value.trim().length > 0);
      };

      // Clearing resets the field and restores the default location order.
      const clearSearch = () => {
        input.value = '';
        list.replaceChildren();
        list.hidden = true;
        syncClearButton();
        this.resetLocationOrder();
        input.focus();
      };

      if (clearBtn) clearBtn.addEventListener('click', clearSearch, { signal: this.signal });
      input.addEventListener('input', () => {
        syncClearButton();
        // Emptying the field manually (backspace/cut) also restores the default order.
        if (input.value.trim().length === 0) this.resetLocationOrder();
      }, { signal: this.signal });

      let places;
      try {
        loadGoogleMaps(this.mapsApiKey);
        // Places API (New) — Data API lets us keep our own input + dropdown.
        places = await window.google.maps.importLibrary('places');
      } catch (err) {
        console.error('[find-in-store] Google Maps failed to load', err);
        return;
      }

      const { AutocompleteSessionToken, AutocompleteSuggestion } = places;

      // A session token groups keystrokes + the final selection into one
      // billable session. A fresh token is started after each selection.
      let sessionToken = new AutocompleteSessionToken();
      let predictions = [];
      let activeIndex = -1;

      const closeList = () => {
        list.replaceChildren();
        list.hidden = true;
        predictions = [];
        activeIndex = -1;
      };

      const selectPrediction = async (prediction) => {
        if (!prediction) return;
        try {
          const place = prediction.toPlace();
          await place.fetchFields({ fields: ['location', 'formattedAddress'], sessionToken });
          input.value = place.formattedAddress || prediction.text?.toString() || input.value;
          syncClearButton();
          closeList();
          sessionToken = new AutocompleteSessionToken();
          const location = place.location;
          if (location) this.sortLocationsByDistance(location.lat(), location.lng());
        } catch (err) {
          console.error('[find-in-store] place lookup failed', err);
        }
      };

      const setActive = (next) => {
        const items = $all(list, SELECTORS.searchSuggestion);
        if (items.length === 0) return;
        activeIndex = (next + items.length) % items.length;
        items.forEach((el, i) => el.classList.toggle('is-active', i === activeIndex));
      };

      const renderSuggestions = (suggestions) => {
        predictions = suggestions.map((s) => s.placePrediction).filter(Boolean);
        activeIndex = -1;
        if (predictions.length === 0) {
          closeList();
          return;
        }

        list.replaceChildren(
          ...predictions.map((prediction) => {
            const li = document.createElement('li');
            li.className = 'find-in-store-drawer__search-suggestion';
            li.setAttribute('role', 'option');
            li.textContent = prediction.text?.toString() || '';
            // mousedown (not click) fires before the input loses focus.
            li.addEventListener('mousedown', (e) => {
              e.preventDefault();
              selectPrediction(prediction);
            });
            return li;
          })
        );
        list.hidden = false;
      };

      const fetchSuggestions = async () => {
        const value = input.value.trim();
        if (value.length < 2) {
          closeList();
          return;
        }
        try {
          const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: value,
            includedRegionCodes: ['dk'],
            language: 'da',
            region: 'dk',
            sessionToken
          });
          renderSuggestions(suggestions || []);
        } catch (err) {
          console.error('[find-in-store] autocomplete failed', err);
        }
      };

      input.addEventListener('input', debounce(fetchSuggestions, 250), { signal: this.signal });

      input.addEventListener('keydown', (e) => {
        if (list.hidden) return;
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActive(activeIndex + 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActive(activeIndex - 1);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          selectPrediction(predictions[activeIndex] ?? predictions[0]);
        } else if (e.key === 'Escape') {
          closeList();
        }
      }, { signal: this.signal });

      // Close the dropdown when clicking outside the search field.
      document.addEventListener('click', (e) => {
        if (!e.target?.closest?.(SELECTORS.searchWrapper)) closeList();
      }, { signal: this.signal });
    }

    sortLocationsByDistance(lat, lng) {
      const container = document.querySelector(SELECTORS.locationsContainer);
      if (!container) return;

      const blocks = $all(container, SELECTORS.locationBlock);
      if (blocks.length === 0) return;

      blocks
        .map((block) => ({
          block,
          distance: haversineKm(
            lat,
            lng,
            Number.parseFloat(block.getAttribute('data-lat')),
            Number.parseFloat(block.getAttribute('data-lng'))
          )
        }))
        .sort((a, b) => a.distance - b.distance)
        // Re-appending in sorted order reorders the existing nodes in place.
        .forEach(({ block }) => container.appendChild(block));

      this.pinFavouriteToTop();
    }

    async loadLocations() {
      const container = document.querySelector(SELECTORS.locationsContainer);
      const template = document.querySelector(SELECTORS.locationTemplate);
      if (!container || !template) return;

      const response = await fetch(ENDPOINTS.locations(this.store));
      const data = await response.json();

      container.innerHTML = '';
      const favouriteStore = window.localStorage.getItem(STORAGE_KEYS.favouriteStore);

      data.forEach((item) => {
        const location = item?.node;
        if (!location) return;

        // Exclude special Shopify location name.
        if (String(location.name).toLowerCase() === 'shop location') return;

        const edges = location?.metafields?.edges || [];

        // Exclude locations explicitly hidden from the drawer.
        if (getMetafieldValue(edges, 'hide_location_from_drawer') === 'true') return;

        const customName = getMetafieldValue(edges, 'location_name') || location.name;
        const workingHours = getMetafieldValue(edges, 'location_working_hours');
        const locationPlace = getMetafieldValue(edges, 'location_place');

        const clone = template.content.cloneNode(true);
        this.fillLocationClone(clone, { location, customName, workingHours, locationPlace, favouriteStore });
        container.appendChild(clone);
      });

      // Remember the initial (server) order so we can restore it when the search is cleared.
      this.defaultLocationOrder = $all(container, SELECTORS.locationBlock);
      this.pinFavouriteToTop();
    }

    // Move the favourite store (location-selected) to the top of the list.
    pinFavouriteToTop() {
      const container = document.querySelector(SELECTORS.locationsContainer);
      if (!container) return;

      const favouriteBlock = $all(container, SELECTORS.locationBlock).find((block) => {
        const btn = $(block, SELECTORS.favouriteButton);
        return btn?.classList.contains('location-selected');
      });

      if (favouriteBlock) container.insertBefore(favouriteBlock, container.firstChild);
    }

    // Re-append blocks in their original order, undoing any distance sort.
    resetLocationOrder() {
      const container = document.querySelector(SELECTORS.locationsContainer);
      if (!container || !this.defaultLocationOrder) return;
      this.defaultLocationOrder.forEach((block) => container.appendChild(block));
      this.pinFavouriteToTop();
    }

    fillLocationClone(clone, { location, customName, workingHours, locationPlace, favouriteStore }) {
      const block = clone.querySelector(SELECTORS.locationBlock);
      if (block) {
        block.setAttribute('data-store', location.name);
        block.setAttribute('data-lat', location?.address?.latitude ?? '');
        block.setAttribute('data-lng', location?.address?.longitude ?? '');
      }

      const nameEl = clone.querySelector('.find-in-store-drawer__location-header__name');
      if (nameEl) nameEl.textContent = customName;

      const addressEl = clone.querySelector('.find-in-store-drawer__location-address');
      if (addressEl) {
        const addressLines = [];
        if (locationPlace) addressLines.push(locationPlace);

        const addr = location.address;
        if (addr) {
          [addr.address1, addr.address2, [addr.zip, addr.city].filter(Boolean).join(' ')].forEach((line) => {
            if (line && String(line).trim() !== '') addressLines.push(String(line));
          });
        }

        setTextLines(addressEl, addressLines);
      }

      if (workingHours) {
        const hoursEl = clone.querySelector('.find-in-store-drawer__location-working-hours__content');
        if (hoursEl) setTextLines(hoursEl, String(workingHours).split('\n'));
      } else {
        const hoursSection = clone.querySelector('.find-in-store-drawer__location-working-hours');
        if (hoursSection) hoursSection.style.display = 'none';
      }

      const favBtn = clone.querySelector(SELECTORS.favouriteButton);
      if (favBtn && favouriteStore && favouriteStore === location.name) {
        favBtn.classList.add('location-selected');
        favBtn.textContent = favBtn.getAttribute('data-favourite-selected') || favBtn.textContent;
      }
    }

    async loadInventory() {
      if (!this.prodId) return;

      const response = await fetch(ENDPOINTS.inventory(this.store, this.prodId));
      const data = await response.json();

      data.forEach((item) => {
        const gid = item?.id;
        if (!gid) return;
        const variantId = String(gid).split('/').pop();
        if (!variantId) return;

        this.inventoryByVariantId.set(variantId, {
          inventory_quantities: item.inventory_quantities || []
        });
      });
    }

    getStockState(level) {
      if (level > this.levelLow) return 'in';
      if (level > this.levelOut) return 'low';
      return 'out';
    }

    renderStockForVariant(variantId) {
      const entry = this.inventoryByVariantId.get(String(variantId));
      if (!entry || !Array.isArray(entry.inventory_quantities)) return;

      entry.inventory_quantities.forEach((loc) => {
        const storeName = loc?.name;
        if (!storeName) return;

        const block = this.drawer.querySelector(`[data-store="${escapeCss(String(storeName))}"]`);
        if (!block) return;

        const iconEl = $(block, SELECTORS.stockIcon);
        const textEl = $(block, SELECTORS.stockText);
        if (!iconEl || !textEl) return;

        const state = this.getStockState(loc?.level ?? 0);
        textEl.textContent = this.stockText[state];

        iconEl.className = `find-in-store-drawer__location-header__stock__icon find-in-store-drawer__location-header__stock__icon--${
          state === 'in' ? 'in-stock' : state === 'low' ? 'low-stock' : 'out-of-stock'
        }`;
      });
    }

    toggleFavourite(clickedButton) {
      const allButtons = $all(this.drawer, SELECTORS.favouriteButton);
      allButtons.forEach((btn) => {
        if (btn === clickedButton) return;
        btn.classList.remove('location-selected');
        btn.textContent = btn.getAttribute('data-favourite-unselected') || btn.textContent;
      });

      clickedButton.classList.toggle('location-selected');

      const isSelected = clickedButton.classList.contains('location-selected');
      const block = clickedButton.closest(SELECTORS.locationBlock);
      const store = block?.getAttribute('data-store') || '';

      if (isSelected) {
        clickedButton.textContent = clickedButton.getAttribute('data-favourite-selected') || clickedButton.textContent;
        if (store) window.localStorage.setItem(STORAGE_KEYS.favouriteStore, store);
        this.pinFavouriteToTop();
      } else {
        clickedButton.textContent =
          clickedButton.getAttribute('data-favourite-unselected') || clickedButton.textContent;
        window.localStorage.removeItem(STORAGE_KEYS.favouriteStore);
        this.resetLocationOrder();
      }
    }

    // Restore the persisted "in stock only" choice so it applies on every
    // product for this session. applyInStockOnlyFilter() runs later (after
    // locations load) and reads this checkbox state.
    restoreInStockOnly() {
      const checkbox = this.drawer.querySelector(SELECTORS.inStockCheckbox);
      if (!checkbox) return;
      checkbox.checked = window.sessionStorage.getItem(STORAGE_KEYS.inStockOnly) === 'true';
    }

    applyInStockOnlyFilter() {
      const checkbox = this.drawer.querySelector(SELECTORS.inStockCheckbox);
      if (!checkbox) return;

      const blocks = $all(this.drawer, SELECTORS.locationBlock);
      const showInStockOnly = Boolean(checkbox.checked);

      blocks.forEach((block) => {
        if (!showInStockOnly) {
          block.style.display = 'flex';
          return;
        }

        // Hide only if we have a rendered out-of-stock icon; if stock hasn't been rendered yet, keep it visible.
        const isOutOfStock = Boolean(block.querySelector('.find-in-store-drawer__location-header__stock__icon--out-of-stock'));
        block.style.display = isOutOfStock ? 'none' : 'flex';
      });
    }

    isDrawerOpen() {
      return this.drawer.classList.contains('is-open') || this.drawer.getAttribute('aria-hidden') === 'false';
    }

    getMainSizeContainer() {
      return document.querySelector(this.mainSizeContainerSelector);
    }

    syncMainToDrawer() {
      // Preserve drawer state while it's open.
      if (this.isDrawerOpen()) return;

      const mainContainer = this.getMainSizeContainer();
      if (!mainContainer) return;

      const mainRadios = $all(mainContainer, 'input[type="radio"]');
      const drawerRadios = $all(document, SELECTORS.drawerSizeRadios);

      mainRadios.forEach((mainRadio) => {
        const mainLabel = mainContainer.querySelector(`label[for="${escapeCss(mainRadio.id)}"]`);
        const mainSpan = mainLabel?.querySelector?.('span');
        if (!mainLabel || !mainSpan) return;

        const sizeValue = mainSpan.textContent.trim();
        const drawerRadio = drawerRadios.find((r) => r.value === sizeValue);
        if (!drawerRadio) return;

        drawerRadio.checked = mainRadio.checked;

        const drawerLabel = document.querySelector(`label[for="${escapeCss(drawerRadio.id)}"]`);
        if (drawerLabel) {
          drawerLabel.classList.toggle('is-disabled', mainLabel.classList.contains('is-disabled'));
        }
      });
    }

    setupMainVariantSync() {
      // Initial sync (delay lets theme scripts finish first).
      window.setTimeout(() => this.syncMainToDrawer(), 100);

      document.addEventListener('change', (event) => {
        // Main form changed -> update drawer
        if (event.target?.closest?.(this.mainSizeContainerSelector)) {
          this.syncMainToDrawerDebounced();
          return;
        }

        // Drawer changed -> update main form
        if (event.target?.matches?.(SELECTORS.drawerSizeRadios)) {
          const selectedSize = event.target.value;
          const mainContainer = this.getMainSizeContainer();
          if (!mainContainer) return;

          const mainRadios = $all(mainContainer, 'input[type="radio"]');
          mainRadios.forEach((mainRadio) => {
            const mainLabel = mainContainer.querySelector(`label[for="${escapeCss(mainRadio.id)}"]`);
            const mainSpan = mainLabel?.querySelector?.('span');
            if (!mainLabel || !mainSpan) return;

            const mainSizeText = mainSpan.textContent.trim();
            if (mainSizeText !== selectedSize || mainRadio.checked) return;

            mainRadio.checked = true;
            mainRadio.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }
      }, { signal: this.signal });

      // Survive Shopify re-renders of the variant picker.
      this.observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          // Injected HTML (theme re-rendering the form)
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            if (mutation.target?.closest?.('.product-info__block-item')) {
              this.syncMainToDrawerDebounced();
              return;
            }
          }

          // Class changes (theme visually disabling variants)
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (mutation.target?.closest?.(this.mainSizeContainerSelector)) {
              this.syncMainToDrawerDebounced();
              return;
            }
          }
        }
      });

      // Scope the observer to the product-info container so its callback only
      // runs on variant-picker mutations, not on every DOM/class change across
      // the page (other apps, animations, sticky headers, etc.). Fall back to
      // document.body if the expected markup isn't present.
      const observeRoot =
        document.querySelector(this.mainSizeContainerSelector)?.closest('.product-info') ||
        document.querySelector('.product-info') ||
        document.body;

      this.observer.observe(observeRoot, {
        childList: true,
        attributes: true,
        subtree: true,
        attributeFilter: ['class']
      });
    }

    // Detach everything this instance wired up so it can be garbage-collected
    // and won't keep firing against detached nodes after a re-render.
    destroy() {
      this.abortController?.abort();
      this.observer?.disconnect();
      this.observer = null;
    }
  }

  let instance = null;

  // (Re)create the drawer against the current DOM, tearing down any previous
  // instance first. Stays null when the markup isn't present, so a later
  // section:load can try again.
  function mount() {
    if (instance) {
      instance.destroy();
      instance = null;
    }
    const next = new FindInStoreDrawer();
    if (next.disabled) return;
    next.init();
    instance = next;
  }

  // Init immediately (theme bundles may be deferred; we don't need DOMContentLoaded for basic selectors).
  mount();

  // Theme-editor lifecycle: sections are re-rendered on every customizer edit,
  // which swaps out our markup but leaves this instance pointing at detached
  // nodes. Re-mount on load/unload so the feature keeps working (and so it
  // initialises if a merchant adds the section after the page first loaded).
  // These events only fire inside the theme editor, so storefront is unaffected.
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', mount);
})();